/** Matches `$cc-nav-height` — used for IO root shrink and legacy hero helpers. */
export const CC_NAV_HERO_ROOT_MARGIN_TOP = 80;
/**
 * Y-offset (viewport px) for which `[data-cc-nav]` section owns the fixed nav theme.
 * Aligns with the nav pill (~$cc-nav-offset-desktop + half pill), not the bottom of the
 * nav band (80px). Using the full band caused hero/footer handoff ~40px too late when
 * scrolling up: light nav text on a dark hero backdrop (or the inverse).
 */
export const CC_NAV_THEME_LINE = 54;
export const CC_NAV_THEME_SELECTOR = "[data-cc-nav]";

/** True when the element occupies the band directly under the fixed nav pill. */
export function isSectionAtNavLine(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const line = CC_NAV_THEME_LINE;
  return rect.top <= line && rect.bottom > line;
}

export function sectionWantsDarkNav(el: HTMLElement): boolean {
  return el.dataset.ccNav === "dark";
}

/** Dark nav when the section currently under the nav bar is marked `data-cc-nav="dark"`. */
export function getNavOverDarkSection(): boolean {
  const line = CC_NAV_THEME_LINE;
  const sections = document.querySelectorAll<HTMLElement>(CC_NAV_THEME_SELECTOR);
  let match: HTMLElement | null = null;
  let matchTop = -Infinity;

  for (const section of sections) {
    if (!isSectionAtNavLine(section)) continue;
    const top = section.getBoundingClientRect().top;
    // At an aligned edge, the closest nested theme owns the surface.
    const nestedAtSameTop = top === matchTop && match !== null &&
      section.parentElement?.closest(CC_NAV_THEME_SELECTOR) === match;
    if (top > matchTop || nestedAtSameTop) {
      matchTop = top;
      match = section;
    }
  }

  if (match) {
    return sectionWantsDarkNav(match);
  }

  // Nav line sits in a seam/gap between `[data-cc-nav]` boxes (e.g. closing CTA → footer).
  // Prefer the nearest section above the line so theme does not flash to light.
  let nearestAbove: HTMLElement | null = null;
  let nearestAboveBottom = -Infinity;

  for (const section of sections) {
    const rect = section.getBoundingClientRect();
    if (rect.bottom <= line && rect.bottom > nearestAboveBottom) {
      nearestAboveBottom = rect.bottom;
      nearestAbove = section;
    }
  }

  return nearestAbove ? sectionWantsDarkNav(nearestAbove) : false;
}

/**
 * DOM-driven nav theme with a route-based fallback before sections are measurable
 * (avoids a light flash on refresh over dark heroes).
 */
export function getNavOverDarkSectionWithFallback(routeFallback: boolean): boolean {
  if (typeof document === "undefined") {
    return routeFallback;
  }

  const sections = document.querySelectorAll<HTMLElement>(CC_NAV_THEME_SELECTOR);
  if (sections.length === 0) {
    return routeFallback;
  }

  return getNavOverDarkSection();
}

/** @deprecated Use isSectionAtNavLine — kept for callers that still pass the hero node. */
export function isHeroIntersecting(hero: HTMLElement): boolean {
  const rect = hero.getBoundingClientRect();
  const rootTop = CC_NAV_HERO_ROOT_MARGIN_TOP;
  const rootBottom = window.innerHeight;

  return rect.bottom > rootTop && rect.top < rootBottom;
}

export function heroWantsDarkNav(el: HTMLElement): boolean {
  return sectionWantsDarkNav(el);
}

export function getHeroIntersectionState(): boolean {
  return getNavOverDarkSection();
}
